// V4-FRONT-ANY-USAGE batch 5 2026-05-28
import { useState } from 'react';
import { ExternalLink, BookOpen, X, Sparkles, Target, Hash, Users, Lightbulb } from 'lucide-react';
import type { TranslationKeys } from '../../../i18n/fr';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SourceRow, getSourceIcon, getRelevanceColor } from './content/SourceRow';
import type { SourceReference } from '../../../types/post';

interface SourceReferencesSectionProps {
  sources?: SourceReference[];
  className?: string;
  /** When provided, "Voir détails" opens FeedItemsModal instead of the built-in dialog */
  onViewItemDetails?: (sourceId: string, title: string) => void;
}

// Valid source type keys for translations
type SourceTypeKey = 'rss' | 'rss_feed' | 'youtube' | 'video' | 'website' | 'url' | 'raw_text' | 'text' | 'document' | 'pdf' | 'unknown';

// Helper to get relevance display info for the detail modal
const getRelevanceInfo = (relevance: number, t: TranslationKeys): { bg: string; text: string; label: string } => {
  const percent = Math.round(relevance * 100);
  if (percent >= 70) return { bg: 'rgba(34, 197, 94, 0.12)', text: 'var(--color-success)', label: t.sources?.relevanceHigh || 'Élevée' };
  if (percent >= 40) return { bg: 'rgba(234, 179, 8, 0.12)', text: '#d97706', label: t.sources?.relevanceMedium || 'Moyenne' };
  return { bg: 'rgba(239, 68, 68, 0.12)', text: 'var(--color-danger)', label: t.sources?.relevanceLow || 'Faible' };
};

export function SourceReferencesSection({ sources, className = '', onViewItemDetails }: SourceReferencesSectionProps) {
  const { t } = useLanguage();
  const [selectedSource, setSelectedSource] = useState<SourceReference | null>(null);
  
  // Don't render if no sources
  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`rounded-xl border overflow-hidden ${className}`} style={{ 
        backgroundColor: 'var(--color-background)',
        borderColor: 'var(--color-border)',
        marginBottom: '1rem'
      }}>
        {/* Header */}
        <div
          className="flex items-center gap-2.5 px-6 py-4"
          style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}
        >
          <div
            className="p-2 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-surface-hover)' }}
          >
            <BookOpen className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
          </div>
          <span className="text-sm font-medium flex-1" style={{ color: 'var(--color-text-primary)' }}>
            {t.sources?.usedForGeneration || 'Sources utilisées pour la génération'}
          </span>
          <span 
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ 
              backgroundColor: 'var(--color-primary-light)',
              color: 'var(--color-primary)'
            }}
          >
            {sources.length}
          </span>
        </div>

        {/* Sources list */}
        <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
          {sources.map((source, index) => (
            <SourceRow
              key={source.id || index}
              source={source}
              t={t}
              onViewDetails={(s) => {
                if (onViewItemDetails && s.id) {
                  onViewItemDetails(s.id, s.title);
                } else {
                  setSelectedSource(s);
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Source Detail Modal */}
      {selectedSource && (
        <Dialog open={!!selectedSource} onOpenChange={(open) => !open && setSelectedSource(null)}>
          <DialogContent 
            className="sm:max-w-lg p-0 overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'var(--color-background)',
              borderColor: 'var(--color-border)',
              borderRadius: '16px',
              maxHeight: 'min(70vh, 600px)'
            }}
          >
            {/* Header — compact, sans le titre (déplacé dans le contenu) */}
            <div 
              className="px-6 pt-5 pb-3 flex-shrink-0"
              style={{ 
                borderBottom: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)'
              }}
            >
              <DialogTitle className="flex items-center gap-3 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: 'var(--color-surface-hover)' }}
                >
                  {(() => {
                    const Icon = getSourceIcon(selectedSource.type);
                    return <Icon className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />;
                  })()}
                </div>
                {t.sources?.sourceDetails || 'Détails de la source'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t.sources?.sourceDetails || 'Détails de la source'}
              </DialogDescription>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-6 flex-1 overflow-y-auto min-h-0">
              {/* Titre complet (non tronqué) */}
              <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--color-text-primary)' }}>
                {selectedSource.title}
              </h3>

              {/* Metadata row: relevance + type */}
              <div className="flex items-center gap-4 flex-wrap">
                {/* Relevance */}
                {selectedSource.relevance !== undefined && (() => {
                  const colors = getRelevanceInfo(selectedSource.relevance, t);
                  return (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                        {t.sources?.relevance || 'Pertinence'}
                      </span>
                      <div 
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
                        style={{ backgroundColor: colors.bg }}
                      >
                        <div 
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: colors.text }}
                        />
                        <span className="text-xs font-semibold" style={{ color: colors.text }}>
                          {Math.round(selectedSource.relevance * 100)}% — {colors.label}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Type */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                    {t.sources?.type || 'Type'}
                  </span>
                  <span 
                    className="text-xs px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: 'var(--color-surface-hover)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    {t.sourceTypes?.[selectedSource.type as SourceTypeKey] || selectedSource.type}
                  </span>
                </div>
              </div>

              {/* URL */}
              {selectedSource.url && (
                <a
                  href={selectedSource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs transition-colors hover:underline"
                  style={{ color: 'var(--color-primary)' }}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate max-w-[350px]">{selectedSource.url}</span>
                </a>
              )}

              {/* AI Summary */}
              {selectedSource.aiSummary && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--color-primary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.aiSummary || 'Résumé IA'}
                    </span>
                  </div>
                  <div 
                    className="p-4 rounded-xl text-sm leading-relaxed"
                    style={{ 
                      backgroundColor: 'rgba(99, 102, 241, 0.06)',
                      color: 'var(--color-text-primary)',
                      border: '1px solid rgba(99, 102, 241, 0.12)'
                    }}
                  >
                    {selectedSource.aiSummary}
                  </div>
                </div>
              )}

              {/* Post Angles */}
              {selectedSource.postAngles && selectedSource.postAngles.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Lightbulb className="w-3.5 h-3.5" style={{ color: '#b45309' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.postAngles || 'Angles éditoriaux'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedSource.postAngles.map((angle, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2.5 py-1 rounded-full"
                        style={{ 
                          backgroundColor: 'rgba(234, 179, 8, 0.1)',
                          color: 'var(--color-text-secondary)',
                          border: '1px solid rgba(234, 179, 8, 0.2)'
                        }}
                      >
                        {angle}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Facts */}
              {selectedSource.facts && selectedSource.facts.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Target className="w-3.5 h-3.5" style={{ color: 'var(--color-success)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.keyFacts || 'Faits clés extraits'}
                    </span>
                    <span 
                      className="text-[10px] px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-muted)' }}
                    >
                      {selectedSource.facts.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedSource.facts.map((fact, factIndex) => (
                      <div 
                        key={factIndex}
                        className="flex items-start gap-2 p-3 rounded-lg text-sm"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        <span 
                          className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5"
                          style={{ 
                            backgroundColor: 'var(--color-primary-light)',
                            color: 'var(--color-primary)'
                          }}
                        >
                          {factIndex + 1}
                        </span>
                        <span className="leading-relaxed">{fact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entities */}
              {selectedSource.entityNames && selectedSource.entityNames.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Users className="w-3.5 h-3.5" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.entities || 'Entités mentionnées'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedSource.entityNames.map((entity, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2.5 py-1 rounded-full"
                        style={{ 
                          backgroundColor: 'var(--color-surface-hover)',
                          color: 'var(--color-text-primary)',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        {entity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {selectedSource.keywords && selectedSource.keywords.length > 0 && (
                <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Hash className="w-3.5 h-3.5" style={{ color: 'var(--color-text-secondary)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {t.sources?.extractedKeywords || 'Mots-clés extraits'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedSource.keywords.map((kw, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ 
                          backgroundColor: 'var(--color-primary-light)',
                          color: 'var(--color-primary)'
                        }}
                      >
                        #{kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div 
              className="px-6 py-4 flex justify-end flex-shrink-0"
              style={{ 
                borderTop: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-surface)'
              }}
            >
              <AnimatedIconButton
                icon={X}
                label={t.common?.close || 'Fermer'}
                onClick={() => setSelectedSource(null)}
                variant="secondary"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
